import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { searchAPI } from '../../services/api';
import styles from './SearchBox.module.css';

const SearchBox = ({
  placeholder = "搜索网盘资源...",
  className = "",
  showSuggestions = true,
  diskSource = "",
  defaultValue = "",
  onSearch
}) => {
  const [query, setQuery] = useState(defaultValue);
  const [suggestions, setSuggestions] = useState([]);
  const [showSuggestionList, setShowSuggestionList] = useState(false);
  const [loading, setLoading] = useState(false);
  const [selectedIndex, setSelectedIndex] = useState(-1);

  const navigate = useNavigate();
  const inputRef = useRef(null);
  const suggestionsRef = useRef(null);

  useEffect(() => {
    setQuery(defaultValue);
  }, [defaultValue]);

  useEffect(() => {
    const getSuggestions = async () => {
      if (!query.trim() || !showSuggestions) {
        setSuggestions([]);
        setShowSuggestionList(false);
        return;
      }

      setLoading(true);
      try {
        const response = await searchAPI.searchSuggestions(query);
        setSuggestions(response.data.data || []);
      } catch (error) {
        console.error('Failed to get suggestions:', error);
        setSuggestions([]);
      }
      setLoading(false);
    };

    const timeoutId = setTimeout(getSuggestions, 300);
    return () => clearTimeout(timeoutId);
  }, [query, showSuggestions]);

  const handleSearch = (searchQuery = query) => {
    if (!searchQuery.trim()) return;

    const params = new URLSearchParams({ q: searchQuery.trim() });
    if (diskSource) {
      params.append('diskSource', diskSource);
    }

    // If onSearch is provided, call it (for triggering refresh on same query)
    if (onSearch) {
      onSearch(searchQuery.trim());
    }

    navigate(`/search?${params.toString()}`);
    setShowSuggestionList(false);
    setSelectedIndex(-1);
  };

  const handleKeyDown = (e) => {
    if (!showSuggestionList || suggestions.length === 0) {
      if (e.key === 'Enter') {
        handleSearch();
      }
      return;
    }

    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault();
        setSelectedIndex(prev =>
          prev < suggestions.length - 1 ? prev + 1 : prev
        );
        break;
      case 'ArrowUp':
        e.preventDefault();
        setSelectedIndex(prev => prev > -1 ? prev - 1 : -1);
        break;
      case 'Enter':
        e.preventDefault();
        if (selectedIndex >= 0) {
          handleSearch(suggestions[selectedIndex]);
        } else {
          handleSearch();
        }
        break;
      case 'Escape':
        setShowSuggestionList(false);
        setSelectedIndex(-1);
        break;
      default:
        break;
    }
  };

  const handleSuggestionClick = (suggestion) => {
    handleSearch(suggestion);
  };

  const handleInputChange = (e) => {
    setQuery(e.target.value);
    setSelectedIndex(-1);
    if (e.target.value.trim() && showSuggestions) {
      setShowSuggestionList(true);
    }
  };

  const handleInputBlur = () => {
    setTimeout(() => setShowSuggestionList(false), 200);
  };

  const handleInputFocus = () => {
    // Don't show suggestions on focus, only when typing
  };

  return (
    <div className={`${styles.searchBox} ${className}`}>
      <div className={styles.inputWrapper}>
        <input
          ref={inputRef}
          type="text"
          value={query}
          onChange={handleInputChange}
          onKeyDown={handleKeyDown}
          onBlur={handleInputBlur}
          onFocus={handleInputFocus}
          placeholder={placeholder}
          className={styles.searchInput}
        />
        <button
          className={styles.searchButton}
          onClick={() => handleSearch()}
          type="button"
        >
          搜索
        </button>
      </div>

      {showSuggestionList && suggestions.length > 0 && (
        <div ref={suggestionsRef} className={styles.suggestionsList}>
          {suggestions.map((suggestion, index) => (
            <div
              key={index}
              className={`${styles.suggestionItem} ${
                index === selectedIndex ? styles.selected : ''
              }`}
              onClick={() => handleSuggestionClick(suggestion)}
            >
              {suggestion}
            </div>
          ))}
        </div>
      )}

      {/* {loading && showSuggestionList && (
        <div className={styles.suggestionsLoading}>
          搜索建议加载中...
        </div>
      )} */}
    </div>
  );
};

export default SearchBox;